import { defineAsyncComponent } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/vue3compat/vue.js?vue3';
import { GlButton, GlTooltipDirective } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import WorkItemLinkChildContents from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/work_items/components/shared/work_item_link_child_contents.vue?vue3';
import { __, s__ } from '~/locale';
import { createAlert } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/alert.js?vue3';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { findHierarchyWidget, getDefaultHierarchyChildrenCount, getItems } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/utils.js?vue3';
import toggleHierarchyTreeChildMutation from '../../graphql/client/toggle_hierarchy_tree_child.mutation.graphql';
import isExpandedHierarchyTreeChildQuery from '../../graphql/client/is_expanded_hierarchy_tree_child.query.graphql';
import getWorkItemTreeQuery from '../../graphql/work_item_tree.query.graphql';
import WorkItemChildrenLoadMore from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/components/shared/work_item_children_load_more.vue?vue3';
export default {
    name: 'WorkItemLinkChild',
    components: {
        GlButton,
        WorkItemChildrenWrapper: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/components/work_item_links/work_item_children_wrapper.vue?vue3')),
        WorkItemLinkChildContents,
        WorkItemChildrenLoadMore
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        glFeatureFlagsMixin()
    ],
    props: {
        canUpdate: {
            type: Boolean,
            required: true
        },
        isGroup: {
            type: Boolean,
            required: false,
            default: false
        },
        issuableGid: {
            type: String,
            required: true
        },
        childItem: {
            type: Object,
            required: true
        },
        hasIndirectChildren: {
            type: Boolean,
            required: false,
            default: true
        },
        workItemType: {
            type: String,
            required: false,
            default: ''
        },
        showClosed: {
            type: Boolean,
            required: false,
            default: true
        },
        hiddenMetadataKeys: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        workItemFullPath: {
            type: String,
            required: false,
            default: ''
        },
        isTopLevel: {
            type: Boolean,
            required: false,
            default: false
        },
        draggedItemType: {
            type: String,
            required: false,
            default: null
        },
        allowedChildrenByType: {
            type: Object,
            required: false,
            default: ()=>{}
        },
        activeChildItemId: {
            type: String,
            required: false,
            default: null
        },
        parentId: {
            type: String,
            required: false,
            default: ''
        },
        contextualViewEnabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'drag',
        'drop',
        'error',
        'mouseout',
        'mouseover',
        'remove-child',
        'toggle-drawer'
    ],
    data () {
        return {
            isExpanded: false,
            isLoadingChildren: false,
            children: [],
            fetchNextPageInProgress: false,
            hierarchyWidget: {},
            childItemId: ''
        };
    },
    apollo: {
        hierarchyWidget: {
            query: getWorkItemTreeQuery,
            variables () {
                return {
                    id: this.childItemId,
                    pageSize: getDefaultHierarchyChildrenCount(),
                    endCursor: '',
                    useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField)
                };
            },
            skip () {
                return !this.childItemId;
            },
            update ({ workItem }) {
                if (workItem) {
                    this.isLoadingChildren = false;
                    const { hasChildren, children } = findHierarchyWidget(workItem);
                    this.children = children.nodes;
                    return {
                        pageInfo: children.pageInfo,
                        count: children.count,
                        nodes: children.nodes,
                        hasChildren
                    };
                }
                this.childItemId = '';
                return {};
            },
            error (error) {
                createAlert({
                    message: s__('Hierarchy|Something went wrong while fetching children.'),
                    captureError: true,
                    error
                });
            },
            result () {
                if (this.hasNextPage && this.children.length === 0) {
                    this.fetchNextPage();
                }
            }
        },
        isExpanded: {
            query: isExpandedHierarchyTreeChildQuery,
            variables () {
                return {
                    id: this.childItem.id
                };
            },
            update (data) {
                this.childItemId = data?.isExpandedHierarchyTreeChild?.id;
                return data?.isExpandedHierarchyTreeChild?.isExpanded || false;
            }
        }
    },
    computed: {
        hasChildren () {
            return findHierarchyWidget(this.childItem).hasChildren || this.hierarchyWidget?.hasChildren;
        },
        shouldExpandChildren () {
            // In case the parent is the same as the child,
            // it is creating a cycle and recursively expanding the tree
            // Issue details: https://gitlab.com/gitlab-org/gitlab/-/issues/498106
            if (this.parentId === this.childItem.id) {
                return false;
            }
            const rolledUpCountsByType = findHierarchyWidget(this.childItem)?.rolledUpCountsByType || [];
            const nrOpenChildren = rolledUpCountsByType.map((i)=>i.countsByState.all - i.countsByState.closed).reduce((sum, n)=>sum + n, 0);
            return this.hasChildren && (nrOpenChildren > 0 || this.showClosed);
        },
        pageInfo () {
            return this.hierarchyWidget.pageInfo || {};
        },
        hasNextPage () {
            return Boolean(this.pageInfo?.hasNextPage);
        },
        endCursor () {
            return this.pageInfo?.endCursor || '';
        },
        childItemType () {
            return this.childItem.workItemType.name;
        },
        chevronType () {
            return this.isExpanded ? 'chevron-down' : 'chevron-right';
        },
        chevronTooltip () {
            return this.isExpanded ? __('Collapse') : __('Expand');
        },
        childItemClass () {
            return {
                '!gl-ml-0': this.hasChildren || !this.hasIndirectChildren && this.isTopLevel,
                'gl-bg-blue-50 hover:gl-bg-blue-50 gl-border-default': this.isActive
            };
        },
        allowedChildTypes () {
            return this.allowedChildrenByType?.[this.childItemType] || [];
        },
        draggedItemTypeIsAllowed () {
            return this.allowedChildTypes.includes(this.draggedItemType);
        },
        showChildrenDropzone () {
            return !this.hasChildren && this.draggedItemTypeIsAllowed;
        },
        displayableChildren () {
            const filterClosed = getItems(this.showClosed);
            return filterClosed(this.children);
        },
        isActive () {
            return this.activeChildItemId === this.childItem.id;
        }
    },
    methods: {
        toggleItem () {
            if (this.children.length === 0 && this.hasChildren) {
                this.isLoadingChildren = true;
                this.childItemId = this.childItem.id;
            }
            this.$apollo.mutate({
                mutation: toggleHierarchyTreeChildMutation,
                variables: {
                    id: this.childItem.id,
                    isExpanded: !this.isExpanded
                }
            });
        },
        async fetchNextPage () {
            if (this.hasNextPage && !this.fetchNextPageInProgress) {
                this.fetchNextPageInProgress = true;
                try {
                    await this.$apollo.queries.hierarchyWidget.fetchMore({
                        variables: {
                            endCursor: this.endCursor
                        }
                    });
                } catch (error) {
                    createAlert({
                        message: s__('Hierarchy|Something went wrong while fetching children.'),
                        captureError: true,
                        error
                    });
                } finally{
                    this.fetchNextPageInProgress = false;
                }
            }
        }
    }
};
